import React, { useState, useEffect } from 'react'
import TitleDraftManager from 'lib/titleDraftManager'
import styles from './drafts-selector.module.scss'

export default function DraftsSelector({ modalParams, closeModal }) {
    const [drafts, setDrafts] = useState([])
    const [hoveredId, setHoveredId] = useState(null)

    useEffect(() => {
        // Load drafts on mount
        const loadedDrafts = TitleDraftManager.load()
        setDrafts(loadedDrafts)
    }, [])

    const handleSelectDraft = draft => {
        // Track draft selection
        window.logEv('SELECT_DRAFT', {
            draft_id: draft?.id,
            draft_age_hours: draft?.timestamp
                ? Math.floor((Date.now() - draft.timestamp) / (1000 * 60 * 60))
                : 0,
            word_count: draft?.wordCount || 0,
            is_starred: !!draft?.starred,
            from_location: 'drafts_selector',
        })

        // Call the callback with the selected draft
        if (modalParams.callback) {
            modalParams.callback(draft)
        }
        closeModal()
    }

    const handleStarDraft = (draftId, event) => {
        event.stopPropagation() // Prevent selection when starring

        // Get current draft state for analytics
        const draft = drafts.find(d => d.id === draftId)

        // Toggle star in storage
        TitleDraftManager.toggleStar(draftId)

        // Track star toggle
        if (draft) {
            window.logEv('TOGGLE_DRAFT_STAR', {
                draft_id: draftId,
                action: draft.starred ? 'unstar' : 'star', // Before toggle
                word_count: draft.wordCount || 0,
                draft_age_hours: draft.timestamp
                    ? Math.floor((Date.now() - draft.timestamp) / (1000 * 60 * 60))
                    : 0,
                from_location: 'drafts_selector',
            })
        }

        // Reload drafts to get updated list with new sort order
        const loadedDrafts = TitleDraftManager.load()
        setDrafts(loadedDrafts)
    }

    const handleDeleteDraft = (draftId, event) => {
        event.stopPropagation() // Prevent selection when deleting

        // Confirm deletion with user
        if (!confirm('Are you sure you want to delete this draft?')) {
            return
        }

        // Get draft info for analytics before deletion
        const draft = drafts.find(d => d.id === draftId)

        // Track draft deletion
        if (draft) {
            window.logEv('DELETE_DRAFT', {
                draft_id: draftId,
                word_count: draft.wordCount || 0,
                was_starred: !!draft.starred,
                draft_age_hours: draft.timestamp
                    ? Math.floor((Date.now() - draft.timestamp) / (1000 * 60 * 60))
                    : 0,
                from_location: 'drafts_selector',
            })
        }

        // Remove from storage
        TitleDraftManager.remove(draftId)

        // Update local state
        setDrafts(drafts.filter(d => d.id !== draftId))
    }

    const formatTimestamp = timestamp => {
        // Use relative time for recent, absolute for old
        const now = Date.now()
        const diff = now - timestamp
        const seconds = Math.floor(diff / 1000)
        const minutes = Math.floor(seconds / 60)
        const hours = Math.floor(minutes / 60)
        const days = Math.floor(hours / 24)

        if (minutes < 1) return 'Just now'
        if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
        if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
        if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`

        return new Date(timestamp).toLocaleDateString()
    }

    const groupDraftsByTime = drafts => {
        const groups = {
            today: [],
            thisWeek: [],
            older: [],
        }

        const now = Date.now()
        const oneDayAgo = now - 24 * 60 * 60 * 1000
        const oneWeekAgo = now - 7 * 24 * 60 * 60 * 1000

        drafts.forEach(draft => {
            if (draft.timestamp > oneDayAgo) {
                groups.today.push(draft)
            } else if (draft.timestamp > oneWeekAgo) {
                groups.thisWeek.push(draft)
            } else {
                groups.older.push(draft)
            }
        })

        return groups
    }

    if (drafts.length === 0) {
        return (
            <div className={styles.emptyState}>
                <p>No drafts saved</p>
                <p className={styles.hint}>Start typing in a new post to create a draft</p>
            </div>
        )
    }

    const groupedDrafts = groupDraftsByTime(drafts)
    const isCurrentDraft = draftId => draftId === modalParams.currentDraftId

    return (
        <div className={styles.draftsSelector}>
            {groupedDrafts.today.length > 0 && (
                <div className={styles.timeGroup}>
                    <h4 className={styles.groupHeader}>Today</h4>
                    {groupedDrafts.today.map(draft => (
                        <DraftItem
                            key={draft.id}
                            draft={draft}
                            isHovered={hoveredId === draft.id}
                            isCurrent={isCurrentDraft(draft.id)}
                            onMouseEnter={() => setHoveredId(draft.id)}
                            onMouseLeave={() => setHoveredId(null)}
                            onClick={() => handleSelectDraft(draft)}
                            onStar={e => handleStarDraft(draft.id, e)}
                            onDelete={e => handleDeleteDraft(draft.id, e)}
                            formatTimestamp={formatTimestamp}
                        />
                    ))}
                </div>
            )}

            {groupedDrafts.thisWeek.length > 0 && (
                <div className={styles.timeGroup}>
                    <h4 className={styles.groupHeader}>This Week</h4>
                    {groupedDrafts.thisWeek.map(draft => (
                        <DraftItem
                            key={draft.id}
                            draft={draft}
                            isHovered={hoveredId === draft.id}
                            isCurrent={isCurrentDraft(draft.id)}
                            onMouseEnter={() => setHoveredId(draft.id)}
                            onMouseLeave={() => setHoveredId(null)}
                            onClick={() => handleSelectDraft(draft)}
                            onStar={e => handleStarDraft(draft.id, e)}
                            onDelete={e => handleDeleteDraft(draft.id, e)}
                            formatTimestamp={formatTimestamp}
                        />
                    ))}
                </div>
            )}

            {groupedDrafts.older.length > 0 && (
                <div className={styles.timeGroup}>
                    <h4 className={styles.groupHeader}>Older</h4>
                    {groupedDrafts.older.map(draft => (
                        <DraftItem
                            key={draft.id}
                            draft={draft}
                            isHovered={hoveredId === draft.id}
                            isCurrent={isCurrentDraft(draft.id)}
                            onMouseEnter={() => setHoveredId(draft.id)}
                            onMouseLeave={() => setHoveredId(null)}
                            onClick={() => handleSelectDraft(draft)}
                            onStar={e => handleStarDraft(draft.id, e)}
                            onDelete={e => handleDeleteDraft(draft.id, e)}
                            formatTimestamp={formatTimestamp}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function DraftItem({
    draft,
    isHovered,
    isCurrent,
    onMouseEnter,
    onMouseLeave,
    onClick,
    onStar,
    onDelete,
    formatTimestamp,
}) {
    return (
        <div
            className={`${styles.draftItem} ${isHovered ? styles.hovered : ''} ${
                isCurrent ? styles.current : ''
            }`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
        >
            <div className={styles.draftContent}>
                <div className={styles.draftPreview}>{draft.content || '(Empty draft)'}</div>
                <div className={styles.draftMeta}>
                    <span className={styles.timestamp}>{formatTimestamp(draft.timestamp)}</span>
                    {draft.wordCount && (
                        <span className={styles.wordCount}>{draft.wordCount} words</span>
                    )}
                    {isCurrent && <span className={styles.currentBadge}>Currently editing</span>}
                </div>
            </div>
            <div className={styles.actionButtons}>
                <button
                    className={`${styles.starButton} ${draft.starred ? styles.starred : ''}`}
                    onClick={onStar}
                    aria-label={draft.starred ? 'Unstar draft' : 'Star draft'}
                    title={draft.starred ? 'Unstar draft' : 'Star draft'}
                >
                    {draft.starred ? '★' : '☆'}
                </button>
                <button
                    className={styles.deleteButton}
                    onClick={onDelete}
                    aria-label="Delete draft"
                >
                    ×
                </button>
            </div>
        </div>
    )
}
